import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  // Public
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },

  // App shell (authenticated)
  {
    path: '/',
    component: () => import('../layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Robots tab
      {
        path: '',
        name: 'robots',
        component: () => import('../views/robots/RobotListView.vue'),
        meta: { title: 'Robots', roles: ['admin', 'operator'] },
      },
      {
        path: 'robots/:id',
        name: 'robot-control',
        component: () => import('../views/robots/RobotControlView.vue'),
        meta: { title: 'Robot Control', roles: ['admin', 'operator'] },
      },
      {
        path: 'robots/:id/settings',
        name: 'robot-settings',
        component: () => import('../views/robots/RobotSettingsView.vue'),
        meta: { title: 'Robot Settings', roles: ['admin', 'operator'] },
      },
      {
        path: 'robots/:id/calibrate',
        name: 'robot-calibrate',
        component: () => import('../views/robots/CalibrationView.vue'),
        meta: { title: 'Sensor Calibration', roles: ['admin', 'operator'] },
      },

      // Field tab
      {
        path: 'field',
        name: 'field-list',
        component: () => import('../views/field/FieldListView.vue'),
        meta: { title: 'Field', roles: ['admin', 'operator'] },
      },
      {
        path: 'field/new',
        name: 'field-setup',
        component: () => import('../views/field/FieldSetupView.vue'),
        meta: { title: 'New Field', roles: ['admin', 'operator'] },
      },
      {
        path: 'field/:id/map',
        name: 'field-map',
        component: () => import('../views/field/FieldMapView.vue'),
        meta: { title: 'Field Map', roles: ['admin', 'operator'] },
      },

      // Mission tab
      {
        path: 'missions',
        name: 'mission-list',
        component: () => import('../views/mission/MissionListView.vue'),
        meta: { title: 'Missions', roles: ['admin', 'operator'] },
      },
      {
        path: 'missions/new',
        name: 'mission-create',
        component: () => import('../views/mission/MissionCreateView.vue'),
        meta: { title: 'New Mission', roles: ['admin', 'operator'] },
      },
      {
        path: 'missions/:id',
        name: 'mission-detail',
        component: () => import('../views/mission/MissionDetailView.vue'),
        meta: { title: 'Mission Detail', roles: ['admin', 'operator'] },
      },

      // Result tab (accessible by all roles)
      {
        path: 'results',
        name: 'result-list',
        component: () => import('../views/result/ResultListView.vue'),
        meta: { title: 'Results', roles: ['admin', 'operator', 'customer'] },
      },
      {
        path: 'results/:missionId',
        name: 'result-detail',
        component: () => import('../views/result/ResultDetailView.vue'),
        meta: { title: 'Result Detail', roles: ['admin', 'operator', 'customer'] },
      },

      // Shared detail pages
      {
        path: 'panels/:id',
        name: 'panel-detail',
        component: () => import('../views/panels/PanelDetailView.vue'),
        meta: { title: 'Panel Detail', roles: ['admin', 'operator', 'customer'] },
      },
    ],
  },

  // Legacy redirects
  { path: '/projects/new', redirect: '/field/new' },
  { path: '/projects/:id/map', redirect: to => `/field/${to.params.id}/map` },

  // Catch-all
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard
router.beforeEach((to, _from, next) => {
  const token = localStorage.getItem('solarsdgs-token')
  const userStr = localStorage.getItem('solarsdgs-user')
  const user = userStr ? JSON.parse(userStr) : null

  // Public routes
  if (to.meta.public) {
    if (token && user) {
      const defaultRoute = user.role === 'customer' ? '/results' : '/'
      return next(defaultRoute)
    }
    return next()
  }

  // Auth required (check parent or own meta)
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth)
  if (requiresAuth) {
    if (!token || !user) {
      return next('/login')
    }

    // Check role access
    const allowedRoles = to.meta.roles as string[] | undefined
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      const fallback = user.role === 'customer' ? '/results' : '/'
      return next(fallback)
    }
  }

  next()
})

export default router
